import React, { useState, useEffect, useRef } from 'react';
import TimerDisplay from './TimerDisplay';
import SessionStats from './SessionStats';
import { storage } from '../../utils/storage';
import { useTheme } from '../../hooks/useTheme';

const PomodoroTimer = () => {
  const [minutes, setMinutes] = useState(25);
  const [seconds, setSeconds] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isBreak, setIsBreak] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [currentTask, setCurrentTask] = useState('');
  const [showMoodCheck, setShowMoodCheck] = useState(false);
  const [preMoodLevel, setPreMoodLevel] = useState(0);
  const [postMoodLevel, setPostMoodLevel] = useState(0);
  const intervalRef = useRef(null);
  const { theme } = useTheme();

  useEffect(() => {
    const saved = storage.get('pomodoroSessions') || [];
    setSessions(saved);
  }, []);

  useEffect(() => {
    if (isActive && (minutes > 0 || seconds > 0)) {
      intervalRef.current = setInterval(() => {
        if (seconds === 0) {
          if (minutes === 0) {
            // Timer complete
            setIsActive(false);
            if (!isBreak) {
              setShowMoodCheck(true);
            } else {
              // Break is over, start new work session
              setMinutes(25);
              setIsBreak(false);
            }
          } else {
            setMinutes(minutes - 1);
            setSeconds(59);
          }
        } else {
          setSeconds(seconds - 1);
        }
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isActive, minutes, seconds, isBreak]);

  const toggleTimer = () => {
    if (!isActive && minutes === 25 && seconds === 0 && !isBreak) {
      // Starting a new session
      setPreMoodLevel(0);
      setShowMoodCheck(true);
    } else {
      setIsActive(!isActive);
    }
  };

  const resetTimer = () => {
    setIsActive(false);
    setMinutes(isBreak ? 5 : 25);
    setSeconds(0);
  };

  const completeSession = () => {
    const session = {
      id: Date.now(),
      date: new Date().toISOString(),
      duration: 25,
      task: currentTask,
      preMood: preMoodLevel,
      postMood: postMoodLevel,
      completed: true
    };
    
    const updated = [...sessions, session];
    setSessions(updated);
    storage.set('pomodoroSessions', updated);
    
    // Start break
    setMinutes(5);
    setSeconds(0);
    setIsBreak(true);
    setIsActive(true);
    setShowMoodCheck(false);
    setCurrentTask('');
  };

  const stressLevels = ['😌', '😊', '😐', '😟', '😰'];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">Focus Timer</h1>
        <p className="text-gray-600">Stay focused with the Pomodoro Technique</p>
      </div>

      <TimerDisplay
        minutes={minutes}
        seconds={seconds}
        isActive={isActive}
        isBreak={isBreak}
        currentTask={currentTask}
        setCurrentTask={setCurrentTask}
        onToggle={toggleTimer}
        onReset={resetTimer}
        theme={theme}
      />

      <SessionStats sessions={sessions} />

      {/* Recent Sessions */}
      <div className={`p-6 rounded-xl ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
        <h3 className="font-semibold mb-4">Recent Sessions</h3>
        <div className="space-y-3">
          {sessions.slice(-5).reverse().map(session => (
            <div 
              key={session.id}
              className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{session.task || 'Focus Session'}</div>
                  <div className="text-sm text-gray-500">
                    {new Date(session.date).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <span>Stress: {stressLevels[session.preMood - 1]} → {stressLevels[session.postMood - 1]}</span>
                  <span className="text-green-500">✓</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mood Check Modal */}
      {showMoodCheck && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-xl p-6 max-w-md w-full`}>
            <h3 className="text-xl font-semibold mb-4">
              {preMoodLevel === 0 ? 'How stressed are you before starting?' : 'How do you feel after the session?'}
            </h3>
            <div className="flex justify-around mb-6">
              {stressLevels.map((emoji, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (preMoodLevel === 0) {
                      setPreMoodLevel(index + 1);
                      setShowMoodCheck(false);
                      setIsActive(true);
                    } else {
                      setPostMoodLevel(index + 1);
                    }
                  }}
                  className={`text-3xl p-3 rounded-lg transition-all hover:bg-gray-100`}
                >
                  {emoji}
                </button>
              ))}
            </div>
            {postMoodLevel > 0 && (
              <button
                onClick={completeSession}
                className="w-full py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                Complete Session
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PomodoroTimer;